<script setup lang="ts">
import AppScrollbar from '@/shared/ui/AppScrollbar/index';
import type { Option, Props } from '@/shared/ui/AppSelect/model/types/appSelect';

defineOptions({
  name: 'AppSelect',
});

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Выберите',
  items: () => [],
  height: '255',
  optionValueKey: 'id',
  optionLabelKey: 'text',
  currentOptionLabelKey: 'text',
  observerSelectorId: '',
  inputId: undefined,
  modelValue: null,
  withInput: false,
  disabled: false,
  isOptionLink: false,
  isClearable: false,
});

const emit = defineEmits<{
  (e: 'update:modelValue', option: Option | null): void;
  (e: 'select', option: Option): void;
  (e: 'input', value: string): void;
  (e: 'focus', event: FocusEvent): void;
  (e: 'blur' | 'clear'): void;
}>();

const isMobile: boolean = useIsMobile();

const isShowList: Ref<boolean> = ref(false);
const isActive: Ref<boolean> = ref(false);
const inputValue: Ref<string> = ref(
  props.modelValue ? String(props.modelValue[props.currentOptionLabelKey] ?? '') : '',
);

const currentLabel: ComputedRef<string> = computed(() =>
  props.modelValue ? String(props.modelValue[props.currentOptionLabelKey] ?? '') : '',
);

const selectButtonLabel: ComputedRef<string | undefined> = computed(() =>
  currentLabel.value || props.placeholder ? undefined : 'Выбрать опцию',
);

const toggleDropdown = (): void => {
  if (props.disabled) return;
  isShowList.value = !isShowList.value;
};

const hideList = (): void => {
  isShowList.value = false;
};

const clickSelectHandler = (event: MouseEvent): void => {
  if (!props.withInput) return;
  const target = event.target instanceof HTMLElement ? event.target : null;
  const isClearButton = target?.closest('[data-clear]');

  if (!isClearButton) {
    isShowList.value = true;
  }
};

const selectOption = (item: Option): void => {
  const value = item.id !== -1 ? item : null;
  emit('update:modelValue', value);
  emit('select', item);
  isShowList.value = false;
};

const inputHandler = (): void => {
  emit('input', inputValue.value);
};

const blurHandler = (): void => {
  isActive.value = false;
  emit('blur');
};

const focusHandler = (event: FocusEvent): void => {
  isActive.value = true;
  emit('focus', event);
};

const clickClearHandler = (): void => {
  inputValue.value = '';
  emit('update:modelValue', null);
  emit('clear');
};

watch(
  () => props.modelValue,
  (value) => {
    inputValue.value = value ? String(value[props.currentOptionLabelKey] ?? '') : '';
  },
);
</script>

<template>
  <div
    v-click-outside="hideList"
    :class="[
      'app-select',
      {
        _mobile: isMobile,
        _opened: isShowList,
        '_with-input': withInput,
        _selected: !!modelValue,
        _disabled: disabled,
        _clearable: isClearable,
      },
    ]"
    @click.prevent="clickSelectHandler"
  >
    <div class="app-select__select">
      <template v-if="!withInput">
        <button
          :class="[
            'app-select__button',
            { _opened: isShowList, _selected: !!modelValue },
          ]"
          :aria-label="selectButtonLabel"
          @click.prevent="toggleDropdown"
        >
          <span
            :class="['app-select__button-text', { _selected: !!modelValue }]"
            v-html="modelValue ? currentLabel : placeholder"
          ></span>
        </button>
      </template>
      <template v-else>
        <input
          :id="inputId"
          v-model="inputValue"
          type="text"
          :class="['app-select__input', { _active: isActive }]"
          :placeholder="placeholder"
          @input="inputHandler"
          @blur="blurHandler"
          @focus="focusHandler"
        />
        <button
          v-if="modelValue && isClearable"
          class="app-select__button _clear"
          data-clear
          aria-label="Очистить поле"
          @click.stop.prevent="clickClearHandler"
        >
          <AppIcon name="Cross" />
        </button>
      </template>
      <AppIcon
        v-if="!(withInput && modelValue && isClearable)"
        :name="disabled ? 'ArrowDownSmallDisable' : 'ArrowDownSmall'"
        class="app-select__arrow"
      />
    </div>
    <div v-show="isShowList" :id="observerSelectorId" class="app-select__options">
      <AppScrollbar :height="height">
        <div class="app-select__options-wrapper">
          <ul v-show="items.length" class="app-select__options-list">
            <li
              v-for="item in items"
              :key="String(item[optionValueKey])"
              :class="[
                'app-select__option',
                {
                  _selected:
                    modelValue && item[optionValueKey] === modelValue[optionValueKey],
                },
              ]"
            >
              <button
                v-if="!isOptionLink"
                class="app-select__option-button"
                :aria-label="String(item[optionLabelKey])"
                @click.stop.prevent="selectOption(item)"
                v-html="item[optionLabelKey]"
              ></button>
              <NuxtLink v-else class="app-select__option-button" :to="item.link">
                <span v-html="item[optionLabelKey]"></span>
              </NuxtLink>
            </li>
          </ul>
          <div v-show="!items.length" class="app-select__error">
            По вашему запросу ничего не найдено...
          </div>
        </div>
      </AppScrollbar>
    </div>
  </div>
</template>

<style lang="less">
.app-select {
  .box-shadow();

  cursor: pointer;
  width: 100%;
  position: relative;
  background: #ffffff;
  border: 1px solid @color-gallery;
  border-radius: @main-size;
  transition: border-color 0.3s ease;

  &._with-input {
    min-height: 45px;
  }

  &__arrow {
    position: absolute;
    top: 50%;
    right: @size-x4;
    transform: translateY(-50%) rotate(0);
    width: @size-x5;
    height: @size-x5;
    background-color: white;
    transition: transform @A ease-in-out;
    z-index: 6;
    pointer-events: none;
  }

  &._opened {
    border: 1px solid @color-persimmon;

    .app-select__arrow {
      transform: translateY(-50%) rotate(180deg);
    }
  }

  &__options {
    position: absolute;
    top: calc(100% + 5px);
    left: 0;
    width: 100%;
    box-shadow: 0px @size-x2 @size-x6 rgba(101, 101, 101, 0.15);
    border-radius: @main-size;
    z-index: 21;
    background-color: white;
  }

  &__button {
    position: relative;
    width: 100%;
    text-align: left;
    z-index: 3;
    padding: @size-x3 @size-x4 @size-x3 @size-x5;

    &._clear {
      display: inline-flex;
      position: absolute;
      top: 50%;
      right: @size-x4;
      transform: translateY(-50%);
      align-items: center;
      justify-content: center;
      width: auto;
      padding: 0;
      border: 0;
      background-color: white;
      z-index: 7;

      .app-icon {
        width: @size-x3;
        height: @size-x3;
      }
    }
  }

  &._disabled &__button {
    pointer-events: none;
  }

  &__button-text {
    font-weight: 500;
    font-size: 14px;
    line-height: 21px;
    color: @color-manatee;

    &._selected {
      color: @color-elephant;
      font-weight: 600;
    }
  }

  &._disabled &__button-text {
    &._selected {
      color: @color-silver-chalice;
    }
  }

  &__options-list {
    padding: @size-x4 0;
  }

  &__option {
    font-weight: 500;
    font-size: 14px;
    line-height: 19px;
    letter-spacing: -0.3px;
  }

  &__option-button {
    display: flex;
    flex-direction: column;
    font-size: inherit;
    text-align: left;
    font-weight: 600;
    width: 100%;
    padding: @size-x2 @size-x6 @size-x2 @size-x5;

    &:hover {
      background-color: @color-black-haze;
    }
  }

  &__input {
    width: 100%;
    outline: 0;
    border: 0;
    height: 100%;
    cursor: pointer;
    font-size: 14px;
    line-height: 21px;
    color: @color-elephant;
    font-weight: 600;
    padding-right: @size-x6;
    text-overflow: ellipsis;
    overflow: hidden;
    border-radius: @main-size;
    transition: border-width 0.6s linear;

    &::placeholder {
      font-weight: 500;
      font-size: 14px;
      line-height: 21px;
      color: @color-manatee;
    }
  }

  &._with-input &__input {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    padding-left: @size-x5;
    padding-right: @size-x8;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__error {
    font-size: 13px;
    font-weight: 400;
    color: @color-manatee;
    padding: @size-x4 @size-x6;
  }

  &._mobile &__button-text {
    font-size: 14px;
    line-height: 21px;
  }
}
</style>
